export const DELIVERY_AREA_CSV_HEADERS = [
  "outward_code",
  "instant_checkout_enabled",
  "delivery_fee_gbp",
] as const

export const MAX_DELIVERY_AREA_IMPORT_ROWS = 2_000
export const MAX_DELIVERY_AREA_IMPORT_BYTES = 1_000_000

export type DeliveryAreaImportRule = {
  outward_code: string
  instant_checkout_enabled: boolean
  delivery_fee_override_pence: number | null
}

export type DeliveryAreaCsvResult = {
  rules: DeliveryAreaImportRule[]
  errors: string[]
}

type CsvRow = { line: number; cells: string[] }

function csvRows(source: string): { rows: CsvRow[]; errors: string[] } {
  const text = source.replace(/^\uFEFF/, "")
  const rows: CsvRow[] = []
  const errors: string[] = []
  let cells: string[] = []
  let field = ""
  let quoted = false
  let afterQuote = false
  let line = 1
  let rowLine = 1

  const finishRow = () => {
    cells.push(field)
    if (cells.some((cell) => cell.trim() !== "")) rows.push({ line: rowLine, cells })
    cells = []
    field = ""
    afterQuote = false
    rowLine = line + 1
  }

  for (let index = 0; index < text.length; index += 1) {
    const character = text[index]
    if (quoted) {
      if (character === '"') {
        if (text[index + 1] === '"') {
          field += '"'
          index += 1
        } else {
          quoted = false
          afterQuote = true
        }
      } else {
        field += character
        if (character === "\n") line += 1
      }
      continue
    }

    if (afterQuote && character !== "," && character !== "\r" && character !== "\n") {
      errors.push(`Line ${line}: unexpected text after a quoted value.`)
      afterQuote = false
    }
    if (character === '"' && field === "") quoted = true
    else if (character === ",") { cells.push(field); field = ""; afterQuote = false }
    else if (character === "\n") { finishRow(); line += 1 }
    else if (character === "\r") {
      if (text[index + 1] === "\n") index += 1
      finishRow(); line += 1
    } else field += character
  }

  if (quoted) errors.push(`Line ${rowLine}: quoted value is not closed.`)
  if (field !== "" || cells.length > 0) finishRow()
  return { rows, errors }
}

function parseBoolean(value: string): boolean | null {
  const normalised = value.trim().toLowerCase()
  if (["true", "yes", "1"].includes(normalised)) return true
  if (["false", "no", "0"].includes(normalised)) return false
  return null
}

export function parseDeliveryFeeGbp(value: string): number | null | undefined {
  const normalised = value.trim()
  if (normalised === "") return null
  if (!/^(?:0|[1-9][0-9]*)(?:\.[0-9]{1,2})?$/.test(normalised)) return undefined
  const [pounds, fractional = ""] = normalised.split(".")
  const pence = Number(pounds) * 100 + Number(fractional.padEnd(2, "0"))
  return Number.isSafeInteger(pence) && pence <= 2_147_483_647 ? pence : undefined
}

export function parseDeliveryAreaCsv(source: string): DeliveryAreaCsvResult {
  const parsed = csvRows(source)
  const errors = [...parsed.errors]
  if (parsed.rows.length === 0) return { rules: [], errors: [...errors, "The CSV is empty."] }

  const headers = parsed.rows[0].cells.map((cell) => cell.trim())
  const required = new Set<string>(DELIVERY_AREA_CSV_HEADERS)
  const unknown = headers.filter((header) => !required.has(header))
  const missing = DELIVERY_AREA_CSV_HEADERS.filter((header) => !headers.includes(header))
  if (new Set(headers).size !== headers.length) errors.push("The CSV contains a duplicate header.")
  if (unknown.length > 0) errors.push(`Unknown CSV column${unknown.length === 1 ? "" : "s"}: ${unknown.join(", ")}.`)
  if (missing.length > 0) errors.push(`Missing CSV column${missing.length === 1 ? "" : "s"}: ${missing.join(", ")}.`)
  if (headers.length !== DELIVERY_AREA_CSV_HEADERS.length) errors.push("The CSV must contain exactly three columns.")
  if (errors.length > 0) return { rules: [], errors }

  const dataRows = parsed.rows.slice(1)
  if (dataRows.length === 0) return { rules: [], errors: ["The CSV does not contain any delivery areas."] }
  if (dataRows.length > MAX_DELIVERY_AREA_IMPORT_ROWS) {
    return { rules: [], errors: [`The CSV contains ${dataRows.length} rows; the maximum is ${MAX_DELIVERY_AREA_IMPORT_ROWS}.`] }
  }

  const indexes = Object.fromEntries(headers.map((header, index) => [header, index]))
  const rules: DeliveryAreaImportRule[] = []
  const seen = new Map<string, number>()
  for (const row of dataRows) {
    if (row.cells.length !== headers.length) {
      errors.push(`Line ${row.line}: expected ${headers.length} columns but found ${row.cells.length}.`)
      continue
    }
    const outwardCode = row.cells[indexes.outward_code].trim().toUpperCase()
    if (!/^([A-Z]{1,2}[0-9][0-9A-Z]?|GIR)$/.test(outwardCode)) {
      errors.push(`Line ${row.line}: ${JSON.stringify(row.cells[indexes.outward_code])} is not a valid outward code.`)
    }
    const firstLine = seen.get(outwardCode)
    if (firstLine !== undefined) errors.push(`Line ${row.line}: ${outwardCode} duplicates line ${firstLine}.`)
    else seen.set(outwardCode, row.line)

    const enabled = parseBoolean(row.cells[indexes.instant_checkout_enabled])
    if (enabled === null) errors.push(`Line ${row.line}: instant_checkout_enabled must be true/false, yes/no or 1/0.`)
    const fee = parseDeliveryFeeGbp(row.cells[indexes.delivery_fee_gbp])
    if (fee === undefined) errors.push(`Line ${row.line}: delivery_fee_gbp must be blank or a non-negative GBP amount with at most two decimal places.`)

    if (/^([A-Z]{1,2}[0-9][0-9A-Z]?|GIR)$/.test(outwardCode) && enabled !== null && fee !== undefined && firstLine === undefined) {
      rules.push({
        outward_code: outwardCode,
        instant_checkout_enabled: enabled,
        delivery_fee_override_pence: fee,
      })
    }
  }
  return { rules: errors.length === 0 ? rules : [], errors }
}
